import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Link2, Mail, Plus, Send, Trash2, X } from 'lucide-react';
import {
  MAX_ROSTER_CONTACTS,
  ROLE_LABELS,
  can,
  parseRosterLines,
  type RosterContact,
  type RosterContactStatus,
  type RosterPlayer,
  type TeamDetail,
  type TeamMember,
} from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { Alert, Button, Dialog, Empty, Field, LoadingBlock, Section, TextArea } from '../ui';

const STATUS: Record<RosterContactStatus, { short: string; long: (c: RosterContact) => string; warn?: boolean }> = {
  joined: { short: 'on the team', long: () => 'On the team' },
  invited: { short: 'invited', long: (c) => `Invite sent${c.invitedAt ? ` ${formatDate(c.invitedAt)}` : ''}` },
  expired: { short: 'invite expired', long: () => 'Invite expired or was replaced', warn: true },
  not_sent: { short: 'not sent', long: () => 'Not sent yet: Hoopify’s email isn’t set up', warn: true },
  held: { short: 'waiting', long: () => 'Sent when the club makes the team live' },
};

/** The players on the team, with or without a login. */
export function RosterTab({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const key = ['roster', team.id];
  const roster = useQuery({ queryKey: key, queryFn: () => api.get<RosterPlayer[]>(`/teams/${team.id}/roster`) });
  const canEdit = can(team.role, 'roster:write');
  const [dialog, setDialog] = useState<'add' | 'paste' | null>(null);
  const [editing, setEditing] = useState<RosterPlayer | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['invites', team.id] });
    void qc.invalidateQueries({ queryKey: ['members', team.id] });
  };

  if (roster.isLoading) return <LoadingBlock />;
  if (roster.isError) return <Alert>{errorMessage(roster.error)}</Alert>;
  const players = roster.data ?? [];

  return (
    <Section
      title="Roster"
      description="Your players, whether or not they have a login. Used for attendance and courtside roll call."
      aside={
        canEdit && players.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setDialog('paste')}>
              <ClipboardList className="size-4" aria-hidden /> Paste a list
            </Button>
            <Button onClick={() => setDialog('add')}>
              <Plus className="size-4" aria-hidden /> Add player
            </Button>
          </div>
        )
      }
    >
      {!players.length ? (
        <Empty
          title="No players yet"
          action={
            canEdit && (
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => setDialog('paste')}>Paste a list of names</Button>
                <Button variant="secondary" onClick={() => setDialog('add')}>
                  Add one player
                </Button>
              </div>
            )
          }
        >
          Add your players by name. They don’t need a login. Add a player’s or parent’s email and they’re invited to the team and linked to that player.
        </Empty>
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {players.map((p) => {
            const waiting = p.contacts.filter((c) => c.status !== 'joined');
            return (
              <li key={p.id}>
                <button
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-left enabled:hover:bg-sunken"
                  onClick={() => setEditing(p)}
                  disabled={!canEdit}
                >
                  <span className="tnum w-8 text-right font-semibold text-muted">{p.number ?? ''}</span>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{p.name}</span>
                    {p.position && <span className="text-[13px] text-muted">, {p.position}</span>}
                    {p.notes && <span className="block truncate text-[13px] text-muted">{p.notes}</span>}
                  </span>
                  {p.links.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[13px] text-muted">
                      <Link2 className="size-3.5" aria-hidden />
                      {p.links.map((l) => `${l.name} (${ROLE_LABELS[l.role].toLowerCase()})`).join(', ')}
                    </span>
                  )}
                  {waiting.length > 0 && (
                    <span className="flex min-w-0 basis-full flex-wrap items-center gap-x-3 gap-y-0.5 pl-12 text-[13px] text-muted">
                      {waiting.map((c) => (
                        <span key={c.id} className={`inline-flex min-w-0 items-center gap-1 ${STATUS[c.status].warn ? 'text-warn' : ''}`}>
                          <Mail className="size-3.5 shrink-0" aria-hidden />
                          <span className="truncate">{c.email}</span>
                          <span className="shrink-0">({STATUS[c.status].short})</span>
                        </span>
                      ))}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {dialog === 'add' && <PlayerDialog team={team} player={null} onClose={() => setDialog(null)} onSaved={refresh} />}
      {dialog === 'paste' && <PasteDialog teamId={team.id} onClose={() => setDialog(null)} onSaved={refresh} />}
      {editing && <PlayerDialog team={team} player={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </Section>
  );
}

function PasteDialog({ teamId, onClose, onSaved }: { teamId: string; onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState('');
  const parsed = parseRosterLines(text);
  const withEmail = parsed.filter((p) => p.email).length;
  const save = useMutation({
    mutationFn: () => api.post(`/teams/${teamId}/roster/bulk`, { text }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} title="Paste a list of players">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        <TextArea
          label="One player per line"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'12 Mia Chen mia@example.com\n7 Ava Smith\nGrace Lee 4'}
          hint="Shirt numbers can go before or after the name, or leave them out. Put a player’s email on the same line to invite them. Parents’ emails go in each player’s details afterwards."
          rows={8}
          autoFocus
        />
        {parsed.length > 0 && (
          <p className="text-[14px] text-muted">
            Adds {parsed.length} {parsed.length === 1 ? 'player' : 'players'}: {parsed.slice(0, 4).map((p) => (p.number ? `${p.name} (${p.number})` : p.name)).join(', ')}
            {parsed.length > 4 ? '…' : ''}
            {withEmail > 0 && ` ${withEmail} will get an email invite.`}
          </p>
        )}
        {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!parsed.length}>
            Add {parsed.length || ''} players
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

type ContactDraft = { key: string; email: string; kind: 'player' | 'parent'; saved?: RosterContact };
const draftsFrom = (p: RosterPlayer | null): ContactDraft[] => (p?.contacts ?? []).map((c) => ({ key: c.id, email: c.email, kind: c.kind, saved: c }));

function PlayerDialog({ team, player, onClose, onSaved }: { team: TeamDetail; player: RosterPlayer | null; onClose: () => void; onSaved: () => void }) {
  const members = useQuery({ queryKey: ['members', team.id], queryFn: () => api.get<TeamMember[]>(`/teams/${team.id}/members`), enabled: !!player });
  const linkable = (members.data ?? []).filter((m) => m.role === 'player' || m.role === 'parent');
  const [v, setV] = useState({ name: player?.name ?? '', number: player?.number ?? '', position: player?.position ?? '', notes: player?.notes ?? '' });
  const [links, setLinks] = useState<string[]>(player?.links.map((l) => l.userId) ?? []);
  const [contacts, setContacts] = useState<ContactDraft[]>(() => draftsFrom(player));
  const [confirmRemove, setConfirmRemove] = useState(false);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });

  const list = () => contacts.filter((c) => c.email.trim()).map((c) => ({ email: c.email.trim().toLowerCase(), kind: c.kind }));
  const save = useMutation({
    mutationFn: async () => {
      const wanted = list();
      // Links first: emails for people already on the team then add their own links on top.
      const before = player?.links.map((l) => l.userId).sort().join() ?? '';
      if (player && before !== [...links].sort().join()) await api.put(`/teams/${team.id}/roster/${player.id}/links`, { userIds: links });
      const changed = !player || JSON.stringify(wanted) !== JSON.stringify(player.contacts.map((c) => ({ email: c.email, kind: c.kind })));
      const body = { ...v, ...(changed ? { contacts: wanted } : {}) };
      if (player) await api.patch<RosterPlayer>(`/teams/${team.id}/roster/${player.id}`, body);
      else await api.post<RosterPlayer>(`/teams/${team.id}/roster`, body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/teams/${team.id}/roster/${player!.id}`),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const f = fieldErrors(save.error);
  const contactError = Object.keys(f).some((k) => k.startsWith('contacts'));

  return (
    <Dialog open onClose={onClose} title={player ? `Edit ${player.name}` : 'Add a player'}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        {(save.isError || remove.isError) && !Object.keys(f).length && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <div className="grid grid-cols-[1fr_80px] gap-3">
          <Field label="Name" value={v.name} onChange={set('name')} error={f.name} maxLength={80} autoFocus />
          <Field label="Number" value={v.number} onChange={set('number')} error={f.number} maxLength={3} className="field-input tnum" />
        </div>
        <Field label="Position (optional)" value={v.position} onChange={set('position')} error={f.position} maxLength={20} placeholder="e.g. Guard" />
        <TextArea label="Coach note (optional)" value={v.notes} onChange={set('notes')} error={f.notes} maxLength={1000} hint="Only coaches and read-only members see this. Don’t store medical details here." />
        <ContactsEditor
          team={team}
          player={player}
          contacts={contacts}
          onChange={setContacts}
          errors={f}
          onResent={(p) => {
            setContacts((now) => now.map((c) => ({ ...c, saved: p.contacts.find((x) => x.id === c.saved?.id) ?? c.saved })));
            onSaved();
          }}
        />
        {player && (
          <fieldset>
            <legend className="text-[14px] font-medium">Linked logins</legend>
            <p className="mt-0.5 text-[13px] text-muted">People who accept an email invite above are linked by themselves. You can also link anyone already on the team, so they can say when {player.name} can’t make it.</p>
            {members.isLoading ? null : linkable.length ? (
              <div className="mt-2 space-y-1">
                {linkable.map((m) => (
                  <label key={m.userId} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-sunken">
                    <input
                      type="checkbox"
                      className="size-4 accent-[var(--action)]"
                      checked={links.includes(m.userId)}
                      onChange={(e) => setLinks(e.target.checked ? [...links, m.userId] : links.filter((x) => x !== m.userId))}
                    />
                    <span>
                      {m.name} <span className="text-[13px] text-muted">({ROLE_LABELS[m.role].toLowerCase()})</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-[14px] text-muted">No players or parents have joined the team yet.</p>
            )}
          </fieldset>
        )}
        {contactError && <Alert>Check the emails above.</Alert>}
        {confirmRemove ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="mr-auto text-[14px]">Remove {player!.name}? Their attendance history is kept, and any invites still waiting are cancelled.</span>
            <Button type="button" variant="secondary" onClick={() => setConfirmRemove(false)}>
              Keep
            </Button>
            <Button type="button" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
              Remove
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            {player && (
              <Button type="button" variant="danger" className="btn-danger mr-auto" onClick={() => setConfirmRemove(true)}>
                <Trash2 className="size-4" aria-hidden /> Remove
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {player ? 'Save' : 'Add player'}
            </Button>
          </div>
        )}
      </form>
    </Dialog>
  );
}

// ---------- Emails ----------

/** Up to three emails for a player, each invited as the player or a parent. */
function ContactsEditor({
  team,
  player,
  contacts,
  onChange,
  errors,
  onResent,
}: {
  team: TeamDetail;
  player: RosterPlayer | null;
  contacts: ContactDraft[];
  onChange: (c: ContactDraft[]) => void;
  errors: Record<string, string>;
  onResent: (p: RosterPlayer) => void;
}) {
  const resend = useMutation({
    mutationFn: (contactId: string) => api.post<RosterPlayer>(`/teams/${team.id}/roster/${player!.id}/contacts/${contactId}/resend`),
    onSuccess: onResent,
  });
  const update = (i: number, patch: Partial<ContactDraft>) => onChange(contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  // Rows with blank emails are skipped when saving, so error indexes count only filled rows.
  let filled = -1;
  return (
    <fieldset>
      <legend className="text-[14px] font-medium">Emails (optional)</legend>
      <p className="mt-0.5 text-[13px] text-muted">
        The player’s own, or a parent’s. Each gets an invite to join {team.name} when you save, and is linked to {player ? player.name : 'this player'} when they accept.
      </p>
      {contacts.length > 0 && (
        <div className="mt-2 space-y-2">
          {contacts.map((c, i) => {
            if (c.email.trim()) filled++;
            const err = c.email.trim() ? (errors[`contacts.${filled}.email`] ?? errors[`contacts.${filled}.kind`]) : undefined;
            const same = c.saved && c.saved.email === c.email.trim().toLowerCase() && c.saved.kind === c.kind;
            const status = same ? c.saved!.status : null;
            return (
              <div key={c.key} className="rounded-md border border-line p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    className={`field-input min-w-[160px] flex-1 py-1.5 text-[14px] ${err ? 'border-danger' : ''}`}
                    value={c.email}
                    onChange={(e) => update(i, { email: e.target.value })}
                    placeholder="name@example.com"
                    aria-label={`Email ${i + 1}`}
                    aria-invalid={!!err}
                    autoComplete="off"
                  />
                  <select className="field-input w-auto py-1.5 text-[14px]" value={c.kind} onChange={(e) => update(i, { kind: e.target.value as 'player' | 'parent' })} aria-label={`Email ${i + 1} belongs to`}>
                    <option value="parent">Parent</option>
                    <option value="player">Player</option>
                  </select>
                  <button type="button" className="btn-ghost p-1.5" onClick={() => onChange(contacts.filter((_, j) => j !== i))} aria-label={`Remove email ${i + 1}`} title="Remove">
                    <X className="size-4" />
                  </button>
                </div>
                {err && <p className="mt-1 text-[13px] text-danger">{err}</p>}
                {status ? (
                  <p className={`mt-1 flex flex-wrap items-center gap-x-2 text-[13px] ${STATUS[status].warn ? 'text-warn' : 'text-muted'}`}>
                    {STATUS[status].long(c.saved!)}
                    {status !== 'joined' && (
                      <button type="button" className="inline-flex items-center gap-1 font-medium text-action hover:underline" onClick={() => resend.mutate(c.saved!.id)} disabled={resend.isPending}>
                        <Send className="size-3.5" aria-hidden /> {resend.isPending && resend.variables === c.saved!.id ? 'Sending…' : 'Send again'}
                      </button>
                    )}
                  </p>
                ) : (
                  c.email.trim() && !err && <p className="mt-1 text-[13px] text-muted">Invited when you save.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
      {errors.contacts && <p className="mt-1 text-[13px] text-danger">{errors.contacts}</p>}
      {resend.isError && (
        <div className="mt-2">
          <Alert>{errorMessage(resend.error)}</Alert>
        </div>
      )}
      {contacts.length < MAX_ROSTER_CONTACTS && (
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-medium text-action hover:underline"
          onClick={() => onChange([...contacts, { key: crypto.randomUUID(), email: '', kind: 'parent' }])}
        >
          <Plus className="size-4" aria-hidden /> Add an email
        </button>
      )}
    </fieldset>
  );
}
